import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, AlertCircle, Package, Edit, Trash2, ArrowUp, ArrowDown, Printer } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import CategoryReferenceGuide from '@/components/CategoryReferenceGuide';
import StockMovementDialog from '@/components/StockMovementDialog';
import LabelPrintDialog from '@/components/LabelPrintDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Inventory() {
  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('wms_user') || '{}'); } catch { return {}; }
  })();
  const isAdmin = ['admin', 'super_admin', 'supervisor'].includes(currentUser?.role);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [nextSku, setNextSku] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [movementDialog, setMovementDialog] = useState({ open: false, product: null, type: 'in' });
  const [filterWarehouse, setFilterWarehouse] = useState('all');
  const [filterStock, setFilterStock] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [printItems, setPrintItems] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    barcode: '',
    quantity: 0,
    warehouse_id: '',
    rack_location: '',
    reorder_point: 10,
    image_url: '',
    inventory_type: 'customer',
    category_id: '',
    cost_price: 0,
    unit_of_measure: 'unit',
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (formData.category_id && !editMode) {
      fetchNextSku(formData.category_id);
    } else {
      setNextSku('');
    }
  }, [formData.category_id, editMode]);

  const fetchData = async () => {
    try {
      const [productsRes, warehousesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/products?inventory_type=customer`),
        axios.get(`${API}/warehouses`),
        axios.get(`${API}/categories?inventory_type=customer`),
      ]);
      setProducts(productsRes.data);
      setWarehouses(warehousesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  };

  const fetchNextSku = async (categoryId) => {
    try {
      const response = await axios.get(`${API}/categories/${categoryId}/next-sku`);
      setNextSku(response.data.next_sku);
    } catch (error) {
      setNextSku('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, inventory_type: 'customer' };
      if (editMode && currentProduct) {
        await axios.patch(`${API}/products/${currentProduct.id}`, payload);
        toast.success('Product updated successfully!');
      } else {
        if (!payload.category_id) {
          toast.error('Please select a category');
          return;
        }
        const res = await axios.post(`${API}/products`, payload);
        toast.success('Product created successfully!');
        // Auto-open print dialog for the newly-created label
        if (res.data?.barcode) {
          setPrintItems([res.data]);
        }
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save product:', error);
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleEdit = (product) => {
    setCurrentProduct(product);
    setFormData({
      name: product.name,
      description: product.description || '',
      barcode: product.barcode || '',
      quantity: product.quantity,
      warehouse_id: product.warehouse_id,
      rack_location: product.rack_location || '',
      reorder_point: product.reorder_point,
      image_url: product.image_url || '',
      inventory_type: 'customer',
      category_id: product.category_id || '',
      cost_price: product.cost_price || 0,
      unit_of_measure: product.unit_of_measure || 'unit',
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await axios.delete(`${API}/products/${id}`);
      toast.success('Product deleted successfully!');
      fetchData();
    } catch (error) {
      console.error('Failed to delete product:', error);
      toast.error('Failed to delete product');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      barcode: '',
      quantity: 0,
      warehouse_id: '',
      rack_location: '',
      reorder_point: 10,
      image_url: '',
      inventory_type: 'customer',
      category_id: '',
      cost_price: 0,
      unit_of_measure: 'unit',
    });
    setEditMode(false);
    setCurrentProduct(null);
    setNextSku('');
  };

  const getWarehouseName = (warehouseId) => {
    const warehouse = warehouses.find((w) => w.id === warehouseId);
    return warehouse ? warehouse.name : 'Unknown';
  };

  const filteredProducts = products.filter((product) => {
    if (filterWarehouse !== 'all' && product.warehouse_id !== filterWarehouse) return false;
    if (filterStock === 'low' && product.quantity > product.reorder_point) return false;
    if (filterStock === 'out' && product.quantity > 0) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      const hitSku = product.sku.toLowerCase().includes(q);
      const hitName = product.name.toLowerCase().includes(q);
      const hitBarcode = (product.barcode || '').toLowerCase().includes(q);
      if (!hitSku && !hitName && !hitBarcode) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1680e5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">Customer Inventory</h1>
          <p className="text-slate-600 mt-2">Manage products for customer orders and fulfillment</p>
        </div>
        <div className="flex items-center gap-2">
          <CategoryReferenceGuide inventoryType="customer" />
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-[#1680e5] hover:bg-[#1680e5]/90" data-testid="add-product-button">
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit Product' : 'Add New Product'}</DialogTitle>
              <DialogDescription>Enter product details below</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="product-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.length === 0 ? (
                        <div className="p-2 text-sm text-slate-500">
                          No categories. Create one in Categories page.
                        </div>
                      ) : (
                        categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name} ({cat.prefix})
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {editMode && (
                    <p className="text-xs text-slate-500">
                      Category can be changed — SKU will be regenerated with the new prefix and updated across all records.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>SKU {editMode ? '' : '(auto-generated)'}</Label>
                  <Input
                    value={editMode ? currentProduct?.sku || '' : (nextSku || 'Select category first')}
                    disabled
                    className="font-mono bg-slate-50"
                    data-testid="product-sku-preview"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Product Name *</Label>
                <Input
                  id="name"
                  placeholder="Product Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="product-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Product description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  data-testid="product-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="barcode">Barcode {!editMode && '(auto-generated)'}</Label>
                  <Input
                    id="barcode"
                    placeholder={editMode ? '' : 'EAN-13 will be generated'}
                    value={formData.barcode}
                    onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                    disabled={!editMode}
                    className={!editMode ? 'font-mono bg-slate-50' : 'font-mono'}
                    data-testid="product-barcode-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="warehouse">Warehouse *</Label>
                  <Select
                    value={formData.warehouse_id}
                    onValueChange={(value) => setFormData({ ...formData, warehouse_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="product-warehouse-select">
                      <SelectValue placeholder="Select warehouse" />
                    </SelectTrigger>
                    <SelectContent>
                      {warehouses.map((warehouse) => (
                        <SelectItem key={warehouse.id} value={warehouse.id}>
                          {warehouse.name} ({warehouse.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rack_location">Rack Location</Label>
                  <Input
                    id="rack_location"
                    placeholder="e.g., A1-B2-S3"
                    value={formData.rack_location}
                    onChange={(e) => setFormData({ ...formData, rack_location: e.target.value.toUpperCase() })}
                    className="font-mono"
                    data-testid="product-rack-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cost_price">Initial Cost/Unit (GHS)</Label>
                  <Input
                    id="cost_price"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="For opening balance"
                    value={formData.cost_price}
                    onChange={(e) => setFormData({ ...formData, cost_price: parseFloat(e.target.value) || 0 })}
                    data-testid="product-cost-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="quantity">Quantity *</Label>
                  <Input
                    id="quantity"
                    type="number"
                    min="0"
                    value={formData.quantity}
                    onChange={(e) =>
                      setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })
                    }
                    required
                    data-testid="product-quantity-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reorder_point">Reorder Point *</Label>
                  <Input
                    id="reorder_point"
                    type="number"
                    min="0"
                    value={formData.reorder_point}
                    onChange={(e) =>
                      setFormData({ ...formData, reorder_point: parseInt(e.target.value) || 0 })
                    }
                    required
                    data-testid="product-reorder-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="image_url">Image URL</Label>
                <Input
                  id="image_url"
                  placeholder="https://example.com/image.jpg"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  data-testid="product-image-input"
                />
              </div>
              <Button type="submit" className="w-full bg-[#1680e5] hover:bg-[#1680e5]/90" data-testid="product-submit-button">
                {editMode ? 'Update Product' : 'Create Product'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="relative">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path></svg>
            <Input
              placeholder="Search by SKU, name, or barcode..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 font-mono"
              data-testid="inventory-search-input"
            />
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <Label>Warehouse</Label>
              <Select value={filterWarehouse} onValueChange={setFilterWarehouse}>
                <SelectTrigger data-testid="filter-warehouse">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Warehouses</SelectItem>
                  {warehouses.map((warehouse) => (
                    <SelectItem key={warehouse.id} value={warehouse.id}>
                      {warehouse.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label>Stock Status</Label>
              <Select value={filterStock} onValueChange={setFilterStock}>
                <SelectTrigger data-testid="filter-stock">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stock</SelectItem>
                  <SelectItem value="low">Low Stock</SelectItem>
                  <SelectItem value="out">Out of Stock</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {filteredProducts.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Package className="h-16 w-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No products yet</h3>
            <p className="text-slate-600 mb-4">Get started by adding your first customer product</p>
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-[#1680e5] hover:bg-[#1680e5]/90"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Product
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>All Products</CardTitle>
            <CardDescription>{filteredProducts.length} product(s) in customer inventory</CardDescription>
          </CardHeader>
          <CardContent>
            {selectedIds.size > 0 && (
              <div className="flex items-center justify-between rounded-md bg-blue-50 border border-blue-200 px-4 py-2 mb-3">
                <span className="text-sm text-blue-900">
                  {selectedIds.size} product{selectedIds.size === 1 ? '' : 's'} selected
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="bg-[#1680e5] hover:bg-[#1680e5]/90"
                    onClick={() => setPrintItems(filteredProducts.filter((p) => selectedIds.has(p.id)))}
                    data-testid="bulk-print-labels-btn"
                  >
                    <Printer className="h-4 w-4 mr-1" />
                    Print selected labels
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>Clear</Button>
                </div>
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    <Checkbox
                      checked={filteredProducts.length > 0 && filteredProducts.every((p) => selectedIds.has(p.id))}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedIds(new Set(filteredProducts.map((p) => p.id)));
                        } else {
                          setSelectedIds(new Set());
                        }
                      }}
                      data-testid="select-all-products"
                    />
                  </TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Warehouse</TableHead>
                  <TableHead>Rack</TableHead>
                  <TableHead>Qty</TableHead>
                  {isAdmin && <TableHead>Avg Cost</TableHead>}
                  {isAdmin && <TableHead>Total Value</TableHead>}
                  <TableHead>Barcode</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map((product) => (
                  <TableRow key={product.id} className="table-row-hover" data-state={selectedIds.has(product.id) ? 'selected' : undefined}>
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(product.id)}
                        onCheckedChange={(checked) => {
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (checked) next.add(product.id); else next.delete(product.id);
                            return next;
                          });
                        }}
                        data-testid={`select-product-${product.sku}`}
                      />
                    </TableCell>
                    <TableCell>
                      <code className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">
                        {product.sku}
                      </code>
                    </TableCell>
                    <TableCell className="font-medium">{product.name}</TableCell>
                    <TableCell>{getWarehouseName(product.warehouse_id)}</TableCell>
                    <TableCell>
                      {product.rack_location ? (
                        <code className="font-mono text-xs bg-slate-100 px-2 py-1 rounded">
                          {product.rack_location}
                        </code>
                      ) : (
                        <span className="text-slate-400 text-xs">Unassigned</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="font-mono font-semibold">{product.quantity}</span>
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-sm font-mono">
                        {product.cost_price ? `GHS ${product.cost_price.toFixed(2)}` : '-'}
                      </TableCell>
                    )}
                    {isAdmin && (
                      <TableCell className="text-sm font-mono">
                        {product.total_value != null ? `GHS ${product.total_value.toFixed(2)}` : '-'}
                      </TableCell>
                    )}
                    <TableCell>
                      {product.barcode ? (
                        <code className="font-mono text-xs text-slate-600">{product.barcode}</code>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {product.quantity === 0 ? (
                        <Badge variant="destructive" className="status-badge">
                          Out of Stock
                        </Badge>
                      ) : product.quantity <= product.reorder_point ? (
                        <Badge className="status-badge bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          Low Stock
                        </Badge>
                      ) : (
                        <Badge className="status-badge bg-green-100 text-green-800 hover:bg-green-100">
                          In Stock
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setPrintItems([product])}
                          title="Print label"
                          data-testid={`print-product-${product.sku}`}
                          disabled={!product.barcode}
                        >
                          <Printer className="h-4 w-4 text-slate-700" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setMovementDialog({ open: true, product, type: 'in' })}
                          title="Inbound Stock"
                          data-testid={`inbound-product-${product.sku}`}
                        >
                          <ArrowUp className="h-4 w-4 text-green-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setMovementDialog({ open: true, product, type: 'out' })}
                          title="Outbound Stock"
                          data-testid={`outbound-product-${product.sku}`}
                        >
                          <ArrowDown className="h-4 w-4 text-red-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(product)}
                          title="Edit"
                          data-testid={`edit-product-${product.sku}`}
                        >
                          <Edit className="h-4 w-4 text-[#1680e5]" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(product.id)}
                          title="Delete"
                          data-testid={`delete-product-${product.sku}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <StockMovementDialog
        open={movementDialog.open}
        onOpenChange={(open) => setMovementDialog({ ...movementDialog, open })}
        product={movementDialog.product}
        movementType={movementDialog.type}
        onSuccess={fetchData}
      />

      <LabelPrintDialog
        open={!!printItems && printItems.length > 0}
        onOpenChange={(o) => { if (!o) setPrintItems(null); }}
        items={printItems || []}
      />
    </div>
  );
}
